// Header.jsx
import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Bell,
  Search,
  User,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown
} from 'lucide-react';

import { useAuth } from '@context/AuthContext';
import { useNotifications } from '@hooks/useNotifications';
import Button, { IconButton } from './Button';

const Header = ({ onToggleSidebar, isSidebarOpen }) => {
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const profileRef = useRef(null);
  const notificationsRef = useRef(null);

  const { user, logout } = useAuth();
  const { notifications = [], unreadCount = 0, markAsRead } = useNotifications();
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
      if (
        notificationsRef.current &&
        !notificationsRef.current.contains(event.target)
      ) {
        setIsNotificationsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
      // Still navigate even if logout fails
      navigate('/login');
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/tickets?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      if (typeof markAsRead === 'function') {
        await markAsRead(notificationId);
      }
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const formatNotificationTime = (timestamp) => {
    try {
      const now = new Date();
      const time = new Date(timestamp);
      const diffInMinutes = Math.floor((now - time) / (1000 * 60));

      if (diffInMinutes < 1) return 'Just now';
      if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
      if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
      return `${Math.floor(diffInMinutes / 1440)}d ago`;
    } catch (error) {
      return 'Unknown time';
    }
  };

  // Ensure onToggleSidebar is called properly
  const handleToggleSidebar = () => {
    console.log('Toggle sidebar clicked', { isSidebarOpen, onToggleSidebar }); // Debug log
    if (typeof onToggleSidebar === 'function') {
      onToggleSidebar();
    } else {
      console.warn('onToggleSidebar is not a function:', onToggleSidebar);
    }
  };

  // inline style objects to match Login.jsx visual language
  const styles = {
    header: {
      backgroundColor: '#fff',
      borderBottom: '1px solid #e6edf3',
      position: 'sticky',
      top: 0,
      zIndex: 40
    },
    container: {
      padding: '0 1.5rem',
      maxWidth: '100%',
      margin: '0 auto'
    },
    row: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      height: '4rem'
    },
    left: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem'
    },
    logoBox: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      textDecoration: 'none'
    },
    logoMark: {
      width: '2rem',
      height: '2rem',
      backgroundColor: 'var(--primary-600, #3b82f6)',
      borderRadius: '0.5rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      fontWeight: 700,
      fontSize: '0.75rem'
    },
    logoText: {
      fontWeight: 600,
      color: 'var(--gray-900, #111827)',
      display: 'none',
      // show on larger screens via media query in CSS; fallback hidden here
      '@media (min-width: 640px)': {
        display: 'block'
      }
    },
    center: {
      flex: 1,
      maxWidth: '36rem',
      margin: '0 1rem'
    },
    searchWrapper: {
      position: 'relative'
    },
    searchIcon: {
      position: 'absolute',
      left: '0.75rem',
      top: '50%',
      transform: 'translateY(-50%)',
      color: 'var(--gray-400, #9ca3af)',
      width: '1rem',
      height: '1rem',
      pointerEvents: 'none'
    },
    searchInput: {
      width: '100%',
      paddingLeft: '2.5rem',
      paddingRight: '0.75rem',
      paddingTop: '0.5rem',
      paddingBottom: '0.5rem',
      fontSize: '0.875rem',
      border: '1px solid var(--gray-300, #d1d5db)',
      borderRadius: '0.5rem',
      outline: 'none',
      transition: 'box-shadow 150ms, border-color 150ms'
    },
    right: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    iconButton: {
      position: 'relative',
      padding: '0.375rem',
      borderRadius: '0.5rem',
      background: 'transparent',
      border: 'none',
      cursor: 'pointer'
    },
    notifBadge: {
      position: 'absolute',
      top: '-0.25rem',
      right: '-0.25rem',
      backgroundColor: 'var(--error-500, #ef4444)',
      color: 'white',
      width: '1.25rem',
      height: '1.25rem',
      borderRadius: '9999px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '0.625rem',
      fontWeight: 700
    },
    dropdown: {
      position: 'absolute',
      right: 0,
      marginTop: '0.5rem',
      width: '20rem',
      background: 'white',
      borderRadius: '0.75rem',
      boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
      border: '1px solid #e6edf3',
      overflow: 'hidden'
    },
    dropdownHeader: {
      padding: '0.75rem 1rem',
      borderBottom: '1px solid #f3f6f9'
    },
    notifItem: {
      padding: '0.75rem 1rem',
      borderBottom: '1px solid #f8fafc',
      cursor: 'pointer',
      display: 'flex',
      gap: '0.75rem',
      transition: 'background-color 150ms'
    },
    notifDot: {
      width: '0.5rem',
      height: '0.5rem',
      borderRadius: '9999px',
      marginTop: '0.5rem',
      flexShrink: 0
    },
    emptyNotifications: {
      padding: '2.5rem 1rem',
      textAlign: 'center',
      color: 'var(--gray-500, #6b7280)'
    },
    profileButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.375rem 0.5rem',
      borderRadius: '0.5rem',
      background: 'transparent',
      border: 'none',
      cursor: 'pointer'
    },
    avatar: {
      width: '2rem',
      height: '2rem',
      borderRadius: '9999px',
      backgroundColor: 'var(--gray-200, #e5e7eb)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    profileDropdown: {
      position: 'absolute',
      right: 0,
      marginTop: '0.5rem',
      width: '14rem',
      background: 'white',
      borderRadius: '0.75rem',
      boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
      border: '1px solid #e6edf3',
      overflow: 'hidden'
    },
    profileHeader: {
      padding: '0.75rem 1rem',
      borderBottom: '1px solid #f3f6f9'
    },
    menuItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.5rem 0.75rem',
      width: '100%',
      background: 'transparent',
      border: 'none',
      textAlign: 'left',
      cursor: 'pointer',
      textDecoration: 'none',
      color: 'var(--gray-700, #374151)',
      fontSize: '0.9375rem',
      transition: 'background-color 150ms'
    },
    signout: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.5rem 0.75rem',
      width: '100%',
      background: 'transparent',
      border: 'none',
      textAlign: 'left',
      cursor: 'pointer',
      color: 'var(--error-600, #dc2626)',
      fontSize: '0.9375rem',
      transition: 'background-color 150ms'
    },
    toggleButton: {
      padding: '0.5rem',
      borderRadius: '0.5rem',
      background: 'transparent',
      border: 'none',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'var(--gray-600, #4b5563)',
      transition: 'background-color 150ms'
    }
  };

  return (
    <motion.header
      style={styles.header}
      initial={{ y: -64 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.28 }}
    >
      <div style={styles.container}>
        <div style={styles.row}>
          {/* Left */}
          <div style={styles.left}>
            <button
              onClick={handleToggleSidebar}
              style={styles.toggleButton}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f3f4f6')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
              aria-label={isSidebarOpen ? 'Close sidebar' : 'Open sidebar'}
              type="button"
            >
              {isSidebarOpen ? (
                <X style={{ width: '1.25rem', height: '1.25rem' }} />
              ) : (
                <Menu style={{ width: '1.25rem', height: '1.25rem' }} />
              )}
            </button>

            <Link to="/dashboard" style={styles.logoBox}>
              <div style={styles.logoMark}>RDX</div>
              <span className="hidden sm:inline-block" style={styles.logoText}>
                Ticketing System
              </span>
            </Link>
          </div>

          {/* Center - Search */}
          <div style={styles.center}>
            <form onSubmit={handleSearch} style={{ width: '100%' }}>
              <div style={styles.searchWrapper}>
                <Search style={styles.searchIcon} />
                <input
                  type="text"
                  placeholder="Search tickets..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={styles.searchInput}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'var(--primary-500, #3b82f6)';
                    e.target.style.boxShadow = '0 0 0 4px rgba(59,130,246,0.06)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'var(--gray-300, #d1d5db)';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
            </form>
          </div>

          {/* Right */}
          <div style={styles.right}>
            {/* Notifications */}
            <div style={{ position: 'relative' }} ref={notificationsRef}>
              <IconButton
                variant="ghost"
                size="sm"
                onClick={() => setIsNotificationsOpen(v => !v)}
                aria-label="Notifications"
              >
                <Bell style={{ width: '1.25rem', height: '1.25rem' }} />
                {unreadCount > 0 && (
                  <motion.span
                    style={styles.notifBadge}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                  >
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </motion.span>
                )}
              </IconButton>

              <AnimatePresence>
                {isNotificationsOpen && (
                  <motion.div
                    style={styles.dropdown}
                    initial={{ opacity: 0, y: -8, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -8, scale: 0.98 }}
                    transition={{ duration: 0.16 }}
                    role="dialog"
                    aria-label="Notifications"
                  >
                    <div style={styles.dropdownHeader}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h3 style={{ margin: 0, fontWeight: 600, color: 'var(--gray-900, #111827)' }}>Notifications</h3>
                        {unreadCount > 0 && (
                          <span style={{ fontSize: '0.875rem', color: 'var(--gray-500, #6b7280)' }}>{unreadCount} unread</span>
                        )}
                      </div>
                    </div>

                    <div style={{ maxHeight: '24rem', overflowY: 'auto' }}>
                      {notifications.length > 0 ? (
                        notifications.slice(0, 10).map(notification => (
                          <div
                            key={notification.id}
                            style={{
                              ...styles.notifItem,
                              backgroundColor: !notification.is_read ? 'rgba(59,130,246,0.04)' : 'transparent'
                            }}
                            onClick={() => handleMarkAsRead(notification.id)}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = notification.is_read
                                ? 'rgba(249, 250, 251, 1)'
                                : 'rgba(59,130,246,0.08)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = notification.is_read
                                ? 'transparent'
                                : 'rgba(59,130,246,0.04)';
                            }}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleMarkAsRead(notification.id);
                              }
                            }}
                          >
                            <div
                              style={{
                                ...styles.notifDot,
                                backgroundColor: !notification.is_read ? 'var(--primary-500, #3b82f6)' : 'var(--gray-300, #d1d5db)'
                              }}
                            />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <p style={{ margin: 0, fontSize: '0.9375rem', fontWeight: 600, color: 'var(--gray-900, #111827)' }}>
                                {notification.title || 'Untitled Notification'}
                              </p>
                              <p style={{ margin: '0.25rem 0', fontSize: '0.875rem', color: 'var(--gray-600, #4b5563)' }}>
                                {notification.message || 'No message available'}
                              </p>
                              <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--gray-400, #9ca3af)' }}>
                                {formatNotificationTime(notification.created_at)}
                              </p>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div style={styles.emptyNotifications}>
                          <Bell style={{ width: '2rem', height: '2rem', color: 'var(--gray-300, #d1d5db)', marginBottom: '0.5rem' }} />
                          <div>No notifications yet</div>
                        </div>
                      )}
                    </div>

                    {notifications.length > 10 && (
                      <div style={{ padding: '0.5rem', borderTop: '1px solid #f3f6f9' }}>
                        <Button
                          variant="ghost"
                          size="sm"
                          fullWidth
                          onClick={() => {
                            navigate('/notifications');
                            setIsNotificationsOpen(false);
                          }}
                        >
                          View all notifications
                        </Button>
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Profile */}
            <div style={{ position: 'relative' }} ref={profileRef}>
              <button
                onClick={() => setIsProfileOpen(v => !v)}
                style={styles.profileButton}
                aria-haspopup="menu"
                aria-expanded={isProfileOpen}
              >
                <div style={styles.avatar}>
                  <User style={{ width: '1rem', height: '1rem', color: 'var(--gray-600, #4b5563)' }} />
                </div>
                <div style={{ display: 'none' }}>
                  <p style={{ margin: 0, fontSize: '0.9375rem', fontWeight: 600, color: 'var(--gray-900, #111827)' }}>
                    {user?.first_name || ''} {user?.last_name || ''}
                  </p>
                  <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--gray-500, #6b7280)', textTransform: 'capitalize' }}>
                    {user?.role || ''}
                  </p>
                </div>
                <ChevronDown style={{ width: '1rem', height: '1rem', color: 'var(--gray-400, #9ca3af)' }} />
              </button>

              <AnimatePresence>
                {isProfileOpen && (
                  <motion.div
                    style={styles.profileDropdown}
                    initial={{ opacity: 0, y: -8, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -8, scale: 0.98 }}
                    transition={{ duration: 0.16 }}
                    role="menu"
                    aria-label="Profile menu"
                  >
                    <div style={styles.profileHeader}>
                      <p style={{ margin: 0, fontWeight: 600, color: 'var(--gray-900, #111827)' }}>
                        {user?.first_name || ''} {user?.last_name || ''}
                      </p>
                      <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.875rem', color: 'var(--gray-500, #6b7280)' }}>
                        {user?.email || ''}
                      </p>
                      <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.75rem', color: 'var(--gray-400, #9ca3af)', textTransform: 'capitalize' }}>
                        {user?.role || ''} • {user?.department || ''}
                      </p>
                    </div>

                    <div style={{ padding: '0.25rem 0' }}>
                      <Link
                        to="/profile"
                        onClick={() => setIsProfileOpen(false)}
                        style={styles.menuItem}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f9fafb')}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                      >
                        <User style={{ width: '1rem', height: '1rem' }} />
                        <span>My Profile</span>
                      </Link>

                      {user?.role === 'admin' && (
                        <Link
                          to="/settings"
                          onClick={() => setIsProfileOpen(false)}
                          style={styles.menuItem}
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f9fafb')}
                          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                        >
                          <Settings style={{ width: '1rem', height: '1rem' }} />
                          <span>Settings</span>
                        </Link>
                      )}
                    </div>

                    <div style={{ borderTop: '1px solid #f3f6f9', padding: '0.25rem 0' }}>
                      <button
                        onClick={handleLogout}
                        style={styles.signout}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#fef2f2')}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                        aria-label="Sign out"
                      >
                        <LogOut style={{ width: '1rem', height: '1rem' }} />
                        <span>Sign out</span>
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </motion.header>
  );
};

export default Header;